import React, { useState } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export interface BandwidthChartSimplePoint {
	formattedTime: string;
	inbound?: number;
	outbound?: number;
}

const INBOUND_COLOR = "var(--color-primitive-brand-surf-300)";
const OUTBOUND_COLOR = "var(--color-primitive-brand-surf-700)";

interface BandwidthChartSimpleProps {
	/** Chart data: each point has formattedTime and optional inbound/outbound values */
	data: BandwidthChartSimplePoint[];
	/** Chart height in pixels */
	height?: number;
	/** Fallback when {@link inboundUnit} / {@link outboundUnit} are not set (e.g. "Mbps") */
	unit?: string;
	/** Unit for inbound series values (API `bandwidthUtilization.inboundUnit`) */
	inboundUnit?: string;
	/** Unit for outbound series values (API `bandwidthUtilization.outboundUnit`) */
	outboundUnit?: string;
}

export function BandwidthChartSimple({
	data,
	height = 220,
	unit = "Mbps",
	inboundUnit: inboundUnitProp,
	outboundUnit: outboundUnitProp,
}: BandwidthChartSimpleProps) {
	const [showInbound, setShowInbound] = useState(true);
	const [showOutbound, setShowOutbound] = useState(true);

	const fallbackUnit = unit.trim() || "Mbps";
	const inboundUnit = (inboundUnitProp?.trim() || fallbackUnit) || "Mbps";
	const outboundUnit = (outboundUnitProp?.trim() || fallbackUnit) || "Mbps";
	const sameYAxisUnit = inboundUnit === outboundUnit;

	const tooltipUnitForSeries = (seriesName: string) => {
		if (seriesName === "Inbound") return inboundUnit;
		if (seriesName === "Outbound") return outboundUnit;
		return fallbackUnit;
	};

	return (
		<div className="w-full">
			<div className="relative" style={{ height }}>
				<ResponsiveContainer width="100%" height="100%">
					<LineChart
						data={data}
						margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
					>
						<CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-semantic-border-primary)" />
						<XAxis
							dataKey="formattedTime"
							tickLine={false}
							axisLine={false}
							tickMargin={8}
							tick={{ fontSize: 11, fill: "var(--color-semantic-text-secondary)" }}
							interval="preserveStartEnd"
						/>
						<YAxis
							tickLine={false}
							axisLine={false}
							tickMargin={8}
							tick={{ fontSize: 11, fill: "var(--color-semantic-text-secondary)" }}
							tickFormatter={(v) => `${v}`}
							label={
								sameYAxisUnit
									? {
											value: inboundUnit,
											angle: -90,
											position: "insideLeft",
											style: {
												fontSize: 10,
												fill: "var(--color-semantic-text-secondary)",
											},
										}
									: undefined
							}
						/>
						<Tooltip
							formatter={(value: number, name: string) => [
								value != null && !Number.isNaN(value)
									? `${value} ${tooltipUnitForSeries(name)}`
									: "—",
								name,
							]}
							contentStyle={{
								fontSize: 12,
								borderRadius: 6,
								border: "1px solid var(--color-semantic-border-primary)",
							}}
							labelStyle={{ color: "var(--color-semantic-text-primary)" }}
						/>
						{showInbound && (
							<Line
								type="monotone"
								dataKey="inbound"
								name="Inbound"
								stroke={INBOUND_COLOR}
								strokeWidth={2}
								dot={{ r: 3 }}
								activeDot={{ r: 5 }}
								connectNulls
							/>
						)}
						{showOutbound && (
							<Line
								type="monotone"
								dataKey="outbound"
								name="Outbound"
								stroke={OUTBOUND_COLOR}
								strokeWidth={2}
								dot={{ r: 3 }}
								activeDot={{ r: 5 }}
								connectNulls
							/>
						)}
					</LineChart>
				</ResponsiveContainer>
			</div>
			<div className="flex flex-wrap items-center gap-3 mt-3">
				<button
					type="button"
					onClick={() => setShowInbound((v) => !v)}
					className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all border ${
						showInbound
							? "border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-primary"
							: "border-transparent text-semantic-text-tertiary"
					}`}
				>
					<span
						className="w-3 h-3 rounded-full flex-shrink-0"
						style={{ backgroundColor: INBOUND_COLOR }}
					/>
					Inbound
				</button>
				<button
					type="button"
					onClick={() => setShowOutbound((v) => !v)}
					className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all border ${
						showOutbound
							? "border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-primary"
							: "border-transparent text-semantic-text-tertiary"
					}`}
				>
					<span
						className="w-3 h-3 rounded-full flex-shrink-0"
						style={{ backgroundColor: OUTBOUND_COLOR }}
					/>
					Outbound
				</button>
			</div>
		</div>
	);
}

export default BandwidthChartSimple;
